// SaleTermsModal — edits the sale terms a property case shows on its
// Overview: asking price, the seller's target, and the listed-on date.
// These live on the linked listing, so the modal fetches the listing,
// edits, and persists via PATCH /agent/listings/{id}, then invalidates
// the cases cache so the Overview card re-hydrates.

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Btn,
  Field,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalShell,
  TextInput,
} from "./ModalShell";
import type { SaleTermsCtx } from "./FlowsProvider";
import { getListing, updateListing, type ListingUpdate } from "@/lib/proagentApi";
import { invalidateCases } from "@/lib/agentCases";

type Form = { asking: string; sellerTarget: string; listedAt: string };

export function SaleTermsModal({
  open,
  ctx,
  onClose,
}: {
  open: boolean;
  ctx: SaleTermsCtx | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const caseObj = ctx?.caseObj;
  const listingId = caseObj?.type === "property" ? caseObj.listingId : undefined;

  const [form, setForm] = useState<Form | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // On open, fetch the listing so the form shows its current values
  // (re-fetched server-side rather than trusting the case denorm).
  useEffect(() => {
    if (!open) return;
    setForm(null);
    setError(null);
    setSaving(false);
    if (!listingId) {
      setError(
        t("agent.flows.saleTerms.needListing", {
          defaultValue: "This case isn't linked to a listing.",
        }) as string,
      );
      return;
    }
    let cancelled = false;
    setLoading(true);
    getListing(listingId)
      .then((l) => {
        if (cancelled) return;
        setForm({
          asking: l.asking_price != null ? String(l.asking_price) : "",
          sellerTarget: l.seller_target_price != null ? String(l.seller_target_price) : "",
          listedAt: l.listed_at ? l.listed_at.slice(0, 10) : "",
        });
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, listingId, t]);

  const set = <K extends keyof Form>(k: K, v: Form[K]) =>
    setForm((f) => (f ? { ...f, [k]: v } : f));

  // Only filled fields are sent — the backend pointer-patch treats an
  // omitted field as "leave unchanged" (clearing a set value isn't
  // supported yet).
  async function handleSave() {
    if (!listingId || !form) return;
    const parsePrice = (s: string): number | undefined => {
      const n = Number(s.replace(/[^\d.]/g, ""));
      return s.trim() && Number.isFinite(n) && n > 0 ? n : undefined;
    };
    const patch: ListingUpdate = {};
    const asking = parsePrice(form.asking);
    const target = parsePrice(form.sellerTarget);
    if (asking != null) patch.asking_price = asking;
    if (target != null) patch.seller_target_price = target;
    if (form.listedAt) patch.listed_at = new Date(form.listedAt).toISOString();

    setSaving(true);
    setError(null);
    try {
      await updateListing(listingId, patch);
      invalidateCases();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSaving(false);
    }
  }

  return (
    <ModalShell open={open} onClose={onClose} width={520} label="Sale terms">
      <ModalHeader
        eyebrow={t("agent.flows.saleTerms.eyebrow", { defaultValue: "Sale terms" })}
        title={t("agent.flows.saleTerms.title", { defaultValue: "Edit sale terms" })}
        hint={
          caseObj?.shortName
            ? t("agent.flows.saleTerms.hintFor", {
                defaultValue: "For {{name}}",
                name: caseObj.shortName,
              })
            : t("agent.flows.saleTerms.hint", {
                defaultValue: "Asking price, the seller's target, and the listed date.",
              })
        }
        onClose={onClose}
      />

      <ModalBody>
        {loading && (
          <div style={{ fontSize: 12.5, color: "var(--ag-ink-3)" }}>
            {t("agent.flows.saleTerms.loading", { defaultValue: "Loading listing…" })}
          </div>
        )}
        {!loading && form && (
          <>
            <Field label={t("agent.flows.saleTerms.asking", { defaultValue: "Asking price (S$)" })}>
              <TextInput
                mono
                type="number"
                step="1000"
                value={form.asking}
                onChange={(v) => set("asking", v)}
                placeholder="1450000"
              />
            </Field>
            <Field
              label={t("agent.flows.saleTerms.sellerTarget", {
                defaultValue: "Seller's target (S$)",
              })}
              hint={t("agent.flows.saleTerms.sellerTargetHint", {
                defaultValue: "What the seller actually wants — kept off the public listing.",
              })}
            >
              <TextInput
                mono
                type="number"
                step="1000"
                value={form.sellerTarget}
                onChange={(v) => set("sellerTarget", v)}
                placeholder="1480000"
              />
            </Field>
            <Field label={t("agent.flows.saleTerms.listedAt", { defaultValue: "Listed on" })}>
              <TextInput
                type="date"
                value={form.listedAt}
                onChange={(v) => set("listedAt", v)}
              />
            </Field>
          </>
        )}
      </ModalBody>

      <ModalFooter
        leftHint={error ? <span style={{ color: "var(--ag-warn)" }}>{error}</span> : null}
      >
        <Btn ghost onClick={onClose} disabled={saving}>
          {t("agent.flows.cancel", { defaultValue: "Cancel" })}
        </Btn>
        <Btn primary onClick={handleSave} disabled={saving || loading || !form}>
          {saving
            ? t("agent.flows.saleTerms.saving", { defaultValue: "Saving…" })
            : t("agent.flows.saleTerms.save", { defaultValue: "Save terms" })}
        </Btn>
      </ModalFooter>
    </ModalShell>
  );
}
